// CompactWorkspaceHero — thin wrapper around the shared WorkspaceHero
// aspect. Pre-formats kicker / meta / caption strings from the
// per-program props and delegates rendering.

import type { CompetitionStatus as DbCompetitionStatus } from "../../../types/database";

import {
  WorkspaceHero,
  type WorkspaceHeroChip,
  type WorkspaceHeroMetaItem,
} from "../../aspects/WorkspaceHero";
import { PANE_THEMES } from "../../brand/paneThemes";

const STUDENT_THEME = PANE_THEMES.Student;

type CompetitionStatus = DbCompetitionStatus | "closed_to_registration";

export type CompactWorkspaceHeroProps = {
  companyName: string;
  programName: string;
  opensAt: string | null;
  closesAt: string | null;
  status: CompetitionStatus;
  teamSizeMin: number;
  teamSizeMax: number;
  deliverablesCount: number;
  submittedCount: number;
  nextDueAt: string | null;
};

function fmtShort(iso: string | null): string {
  if (!iso) return "TBD";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "TBD";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function yearFor(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return String(d.getFullYear());
}

function statusChip(status: CompetitionStatus): WorkspaceHeroChip {
  if (status === "open") return { label: "Active", tone: "sage" };
  if (status === "closed_to_registration") return { label: "Active", tone: "sage" };
  if (status === "judging") return { label: "Judging", tone: "amber" };
  if (status === "closed") return { label: "Closed", tone: "grey" };
  return { label: "Draft", tone: "grey" };
}

export function CompactWorkspaceHero({
  companyName,
  programName,
  opensAt,
  closesAt,
  status,
  teamSizeMin,
  teamSizeMax,
  deliverablesCount,
  submittedCount,
  nextDueAt,
}: CompactWorkspaceHeroProps) {
  const year = yearFor(opensAt) ?? yearFor(closesAt);
  const kicker = year
    ? `${companyName.toUpperCase()} · ${year}`
    : companyName.toUpperCase();
  const teamSizeLabel =
    teamSizeMin === teamSizeMax
      ? `Teams of ${teamSizeMax}`
      : `Teams of ${teamSizeMin}–${teamSizeMax}`;
  const closesLabel = closesAt ? `Closes ${fmtShort(closesAt)}` : "TBD";
  const pct = deliverablesCount === 0 ? 0 : submittedCount / deliverablesCount;
  const caption =
    deliverablesCount === 0
      ? "Deliverables pending"
      : `${submittedCount} of ${deliverablesCount} deliverables done`;
  const meta: WorkspaceHeroMetaItem[] = [
    { icon: "users", label: teamSizeLabel },
    { icon: "calendar", label: closesLabel },
  ];

  return (
    <WorkspaceHero
      theme={STUDENT_THEME}
      kicker={kicker}
      title={programName}
      chip={statusChip(status)}
      meta={meta}
      progressPct={pct}
      progressCaption={caption}
      nextDueLabel={
        nextDueAt && submittedCount < deliverablesCount
          ? `Next due ${fmtShort(nextDueAt)}`
          : undefined
      }
    />
  );
}
